import { themeColor } from '@config/theme';
import { Avatar, Box, Typography } from '@mui/material';
import { Nullable } from '@utils/util.types';
import React from 'react';
import { Channel } from '../types';
import { getFullUrl } from '../ChatWindow/utils/messageUtils';

/**
 * Formats the message timestamp based on when it was sent:
 * - Today: HH:MM format
 * - Yesterday: "Yesterday"
 * - Before Yesterday: MM/DD/YYYY format
 */
const formatMessageTime = (timestamp: number | string): string => {
	// Convert seconds to milliseconds for JavaScript Date
	const timestampMs = typeof timestamp === 'number' ? timestamp * 1000 : timestamp;
	const messageDate = new Date(timestampMs);
	const now = new Date();

	// Reset hours to compare just the dates
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);

	// Compare dates
	const messageDay = new Date(
		messageDate.getFullYear(),
		messageDate.getMonth(),
		messageDate.getDate()
	);

	if (messageDay.getTime() === today.getTime()) {
		// Message from today - show hour:minute
		return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	} else if (messageDay.getTime() === yesterday.getTime()) {
		// Message from yesterday
		return 'Yesterday';
	} else {
		// Message from before yesterday - show date
		return messageDate.toLocaleDateString();
	}
};

const styles = {
	userBox: (isActive: boolean) => ({
		padding: 2,
		display: 'flex',
		alignItems: 'center',
		borderBottom: `1px solid ${themeColor.offWhite}`,
		cursor: 'pointer',
		backgroundColor: isActive ? themeColor.veryLightGray : themeColor.contrastTextWhite,
	}),
	userInfo: {
		marginLeft: 2,
		display: 'flex',
		flexDirection: 'column' as const,
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		gap: '5px',
	},
	name: {
		fontSize: '16px',
		fontWeight: 400,
		color: themeColor.darkGray,
	},
	lastMessage: {
		fontSize: '12px',
		fontWeight: 400,
		color: themeColor.gray,
		maxWidth: '180px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	time: (hasNotification: boolean) => ({
		marginLeft: 'auto',
		fontSize: '12px',
		fontWeight: 400,
		color: hasNotification ? themeColor.teal : themeColor.gray,
		display: 'flex',
		alignSelf: 'flex-start',
	}),
	notificationCount: {
		marginLeft: 'auto',
		fontSize: '12px',
		fontWeight: 400,
		color: themeColor.contrastTextWhite,
		backgroundColor: themeColor.teal,
		borderRadius: '50%',
		minWidth: '20px',
		height: '20px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '0 6px',
	},
};

interface ChannelItemProps {
	channel: Channel;
	lastMessage: Nullable<string>;
	isSelected: boolean;
	onClick: () => void;
}

export const ChannelItem: React.FC<ChannelItemProps> = ({
	channel,
	lastMessage,
	isSelected,
	onClick,
}) => {
	return (
		<Box sx={styles.userBox(isSelected)} onClick={onClick}>
			{/* User Avatar */}
			<Avatar src={channel.image ? getFullUrl(channel.image) : ''} alt={channel.name} />
			{/* User Info */}
			<Box sx={styles.userInfo}>
				<Typography variant="body1" sx={styles.name}>
					{channel.name}
				</Typography>
				<Typography variant="body2" sx={styles.lastMessage}>
					{lastMessage}
				</Typography>
			</Box>

			{/* Message Time and Notifications */}
			<Box
				sx={{
					marginLeft: 'auto',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
				}}
			>
				<Typography variant="body2" sx={styles.time(channel.notificationCount > 0)}>
					{formatMessageTime(channel.lastMessageTimestamp)}
				</Typography>
				{channel.notificationCount > 0 && (
					<Typography variant="body2" sx={styles.notificationCount}>
						{channel.notificationCount}
					</Typography>
				)}
			</Box>
		</Box>
	);
};
